"use client";

import React, { useEffect, useRef, useState } from "react";
import { useAnnouncementsQuery } from "@/queries/announcements/useAnnouncementsQuery";
import { Announcement } from "@/types/announcement";

type AnnouncementStorageItem = {
  views: number;
  lastShownAt: number | null;
};

const STORAGE_KEY = "announcements";

const AnnouncementModal = React.lazy(() => import("./AnnouncementModal"));

export function AnnouncementRenderer() {
  const { data: announcements = [] } = useAnnouncementsQuery();

  const [current, setCurrent] = useState<Announcement | null>(null);
  const [open, setOpen] = useState(false);

  const timeoutRef = useRef<number | null>(null);

  const [storage, setStorage] = useState<
    Record<string, AnnouncementStorageItem>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  });

  const sessionShownRef = useRef(new Set<number>());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    } catch {}
  }, [storage]);

  const canShow = (
    a: Announcement,
    item?: AnnouncementStorageItem
  ) => {
    if (!a.isActive) return false;

    const now = Date.now();

    if (a.showFrom && now < new Date(a.showFrom).getTime()) return false;
    if (a.showUntil && now > new Date(a.showUntil).getTime()) return false;

    if (
      a.maxViews &&
      a.maxViews > 0 &&
      (item?.views ?? 0) >= a.maxViews
    ) {
      return false;
    }

    // If repeatAfterMinutes > 0, enforce interval across sessions via persisted lastShownAt
    if (a.repeatAfterMinutes != null && a.repeatAfterMinutes > 0) {
      if (item?.lastShownAt) {
        const next = item.lastShownAt + a.repeatAfterMinutes * 60 * 1000;
        if (now < next) return false;
      }
    } else {
      // For null/0 repeat — show only once per browser session (do not persist lastShownAt)
      if (sessionShownRef.current.has(a.id)) return false;
    }

    return true;
  };

  useEffect(() => {
    if (current) return;

    const available = announcements.find((a) =>
      canShow(a, storage[String(a.id)])
    );

    if (!available) return;

    setCurrent(available);

    const delay = available.showDelaySeconds ?? 0;

    const show = () => {
      sessionShownRef.current.add(available.id);
      setOpen(true);
    };

    if (delay > 0) {
      timeoutRef.current = window.setTimeout(show, delay * 1000);
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    show();
  }, [announcements, storage, current]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClose = () => {
    if (!current) return;

    const prev = storage[String(current.id)];

    // persist only when needed: either repeatAfterMinutes > 0 (interval across sessions)
    // or when maxViews is set (we need to count views across sessions)
    const shouldPersist =
      (current.maxViews && current.maxViews > 0) ||
      (current.repeatAfterMinutes != null && current.repeatAfterMinutes > 0);

    if (shouldPersist) {
      setStorage({
        ...storage,
        [String(current.id)]: {
          views: (prev?.views || 0) + 1,
          lastShownAt: Date.now(),
        },
      });
    } else {
      // Do not persist to localStorage — only mark shown for this session
      sessionShownRef.current.add(current.id);
    }

    setOpen(false);
    setCurrent(null);
  };

  if (!current) return null;

  return (
    <>
      {open && (
        <React.Suspense fallback={null}>
          <AnnouncementModal
            open={open}
            onClose={handleClose}
            announcement={current as any}
          />
        </React.Suspense>
      )}
    </>
  );
}